'use client';
import { memo, useEffect } from 'react';
import { Collapse, GridItem, HStack, Text, useDisclosure, VStack } from '@chakra-ui/react';

import ProjectsCasesIllustration from 'shared/ProjectsCasesIllustration';
import CustomTags from 'shared/CustomProjectCasesCardsTags';
import CustomOverlayLink from 'shared/CustomOverlayLink';
import { useMobileDevice } from 'shared/hooks';
import NextImage from 'shared/NextImage';

import { DEFAULT_BORDER } from 'constants/global';
import { PAGES_TYPES } from 'constants/pagesTypes';
import Arrow from 'assets/icons/Arrow45degGray.svg';

const collapseTransition = { duration: 0.4 };

const SmallProjectsCasesCard = ({
  companyLogo,
  companyLogoProps,
  domain,
  illustration,
  title,
  link,
  isBigCard = null,
  description = null,
  isCompactDesign = false,
  cardScreen = null,
  ...props
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isMobileDevice } = useMobileDevice();

  useEffect(() => {
    if (isMobileDevice) {
      !isOpen && onOpen();
    }
    if (!isMobileDevice) {
      onOpen && onClose();
    }
  }, [isMobileDevice]);

  return (
    <GridItem
      display="flex"
      flexDir="column"
      pos="relative"
      w={isCompactDesign ? { mobile: 'full', xm: '342px', laptop: '380px' } : 'full'}
      minH={
        isCompactDesign
          ? { mobile: '312px', laptop: '286px' }
          : { mobile: '385px', laptop: '510px', desktop: '535px' }
      }
      border={DEFAULT_BORDER}
      borderColor="line.gray"
      borderRadius={isCompactDesign ? '32px' : { mobile: '32px', laptop: '48px' }}
      gap={isCompactDesign ? '16px' : { mobile: '28px', laptop: '40px', desktop: '48px' }}
      p={
        isCompactDesign
          ? '12px 12px 18px'
          : { mobile: '12px 12px 28px', laptop: '12px 12px 40px', desktop: '12px 12px 48px' }
      }
      onMouseEnter={isMobileDevice ? undefined : onOpen}
      onMouseLeave={isMobileDevice ? undefined : onClose}
      {...props}
    >
      <CustomOverlayLink
        title="Перейти на страницу кейса"
        link={`/${PAGES_TYPES.cases}/${link}`}
        isExternal
        onFocus={onOpen}
        onBlur={onClose}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && e.stopPropagation()}
      />

      <ProjectsCasesIllustration
        cardScreen={cardScreen}
        illustration={illustration}
        flex="1"
        borderRadius={isCompactDesign ? '20px' : { mobile: '20px', laptop: '36px' }}
      />

      <CustomTags
        companyLogo={companyLogo}
        companyLogoProps={companyLogoProps}
        domain={domain}
        pos="absolute"
        inset="24px auto auto 24px"
        isCompactDesign={isCompactDesign}
      />

      <VStack
        w="full"
        align="start"
        justify="space-between"
        gap="0"
        minH={
          isCompactDesign
            ? { mobile: '96.8px', laptop: '50.4px' }
            : { tablet: '84.2px', laptop: '57.6px', desktop: '78px' }
        }
        px={isCompactDesign ? '12px' : { mobile: '12px', laptop: '36px' }}
      >
        <Text
          w="full"
          textStyle={isCompactDesign ? 'h2' : 'header.h4'}
          fontWeight="500"
          fontSize={
            isCompactDesign
              ? { mobile: '16px', laptop: '18px' }
              : { mobile: '18px', laptop: '24px' }
          }
          noOfLines={2}
        >
          {title}
        </Text>

        {isBigCard && description && (
          <Text w="full" textStyle="h2" fontWeight="500" pt="16px" pb="4px" color="text.steelGray">
            {description}
          </Text>
        )}

        <Collapse
          in={isOpen}
          unmountOnExit
          transition={{ exit: collapseTransition, enter: collapseTransition }}
          style={{ width: '100%' }}
        >
          <HStack
            w="full"
            align="center"
            justify="space-between"
            pos="relative"
            minH="40px"
            mt={
              isCompactDesign
                ? { mobile: '12px', laptop: '16px' }
                : { mobile: '16px', laptop: '32px' }
            }
          >
            <Text
              textStyle="h1"
              fontSize={
                isCompactDesign
                  ? { mobile: '16px', laptop: '18px' }
                  : { mobile: '16px', laptop: '20px', desktop: '24px' }
              }
              fontWeight="500"
              color="text.steelGray"
            >
              Подробнее
            </Text>

            <NextImage
              src={Arrow}
              alt="Иконка стрелки"
              w="auto"
              h="auto"
              boxSize={isCompactDesign ? '40px' : { mobile: '36px', laptop: '57px' }}
            />
          </HStack>
        </Collapse>
      </VStack>
    </GridItem>
  );
};

export default memo(SmallProjectsCasesCard);
